import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, RefreshCw } from "lucide-react";
import { AdminSidebar } from "../components/admin/AdminSidebar";
import { EarningsReport } from "../components/admin/EarningsReport";
import { adminApi, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ADMIN_CONFIG } from "../components/admin/adminConfig";
import { LeadFilters } from "../components/admin/LeadFilters";
import { LeadTable } from "../components/admin/LeadTable";
import { SummaryCards } from "../components/admin/SummaryCards";
import { KycReviewDialog } from "../components/admin/KycReviewDialog";
import { TeamDialog } from "../components/admin/TeamDialog";
import { ActivityDialog } from "../components/admin/ActivityDialog";
import { PricingDialog } from "../components/admin/PricingDialog";
import { WalletsDialog } from "../components/admin/WalletsDialog";
import { OwnerAlerts } from "../components/admin/OwnerAlerts";
import { DeleteLeadDialog } from "../components/admin/DeleteLeadDialog";
import { TelegramDialog } from "../components/admin/TelegramDialog";
import { HousekeepingDialog } from "../components/admin/HousekeepingDialog";
import { PaymentsDialog } from "../components/admin/PaymentsDialog";
import { ExitsDialog } from "../components/admin/ExitsDialog";

const LIMIT = 25;

const toParams = (filters) => Object.fromEntries(Object.entries(filters).filter(([, v]) => v));

const useDebounced = (value, ms) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [kind, setKind] = useState("employer");
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [reviewing, setReviewing] = useState(null);
  const [teamOpen, setTeamOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [walletsOpen, setWalletsOpen] = useState(false);
  const [exitsOpen, setExitsOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [telegramOpen, setTelegramOpen] = useState(false);
  const [hkOpen, setHkOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const debounced = useDebounced(filters, 300);
  const params = useMemo(() => toParams(debounced), [debounced]);
  const config = ADMIN_CONFIG[kind];
  const pages = Math.max(1, Math.ceil(data.total / LIMIT));

  useEffect(() => {
    let stale = false;
    setLoading(true);
    adminApi.get(`/admin/leads/${kind}`, { params: { ...params, page, limit: LIMIT } })
      .then((r) => { if (!stale) setData(r.data); })
      .catch((e) => { toast.error(errorMessage(e)); if (e.response?.status === 401) logout(); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [kind, params, page, refreshKey, logout]);

  useEffect(() => { setPage(1); }, [params, kind]);

  const switchKind = (k) => { if (k !== kind) setData({ items: [], total: 0 }); setKind(k); setFilters({}); };
  const [nav, setNav] = useState("dashboard");
  const [newBookings, setNewBookings] = useState(0);
  const [pendingExits, setPendingExits] = useState(0);
  const loadExits = useCallback(() => {
    if (user?.is_owner) adminApi.get("/admin/exits/pending-count").then((r) => setPendingExits(r.data.count)).catch(() => {});
  }, [user?.is_owner]);
  useEffect(() => {
    loadExits();
    const t = setInterval(loadExits, 30000);
    return () => clearInterval(t);
  }, [loadExits]);
  useEffect(() => {
    const load = () => adminApi.get("/admin/hk/new-count").then((r) => setNewBookings(r.data.count)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  const OPEN = { housekeeping: (v) => { setHkOpen(v); setNewBookings(0); adminApi.post("/admin/hk/seen").catch(() => {}); }, payments: setPayOpen, wallets: setWalletsOpen, exits: setExitsOpen, pricing: setPricingOpen, telegram: setTelegramOpen, team: setTeamOpen, activity: setActivityOpen };
  const onNav = (k) => {
    if (OPEN[k]) { OPEN[k](true); return; }
    setNav(k);
    if (k !== "dashboard") switchKind(k);
  };

  const exportCsv = async () => {
    try {
      const r = await adminApi.get(`/admin/leads/${kind}/export`, { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `balangir_${kind}_leads.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] font-body text-slate-100">
      <AdminSidebar user={user} isOwner={user?.is_owner} active={nav === "dashboard" ? "dashboard" : kind} onSelect={onNav} onLogout={logout} badges={{ housekeeping: newBookings, exits: pendingExits }} />
      <main className="mx-auto max-w-[1600px] space-y-6 px-4 py-8 sm:px-6 lg:ml-64">
        {user?.is_owner && <OwnerAlerts onOpenActivity={() => setActivityOpen(true)} />}
        {nav === "dashboard" && <SummaryCards refreshKey={refreshKey} />}
        {nav === "dashboard" && user?.is_owner && <EarningsReport refreshKey={refreshKey} />}
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex">
            {Object.entries(ADMIN_CONFIG).map(([k, c]) => (
              <button key={k} onClick={() => { switchKind(k); setNav(k); }} data-testid={`admin-tab-${k}`}
                className={`border px-5 py-2.5 text-sm font-semibold transition-colors ${kind === k ? "border-orange-500 bg-orange-500/10 text-orange-300" : "border-white/10 text-slate-400 hover:text-white"}`}>
                {c.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setRefreshKey((x) => x + 1)} data-testid="admin-refresh-button"
              className="flex h-10 items-center gap-2 border border-white/15 px-4 text-sm text-slate-300 hover:border-white/40">
              <RefreshCw className="h-4 w-4" />Refresh
            </button>
            <button onClick={exportCsv} data-testid="admin-export-button"
              className="flex h-10 items-center gap-2 bg-orange-500 px-4 text-sm font-semibold text-white transition-colors hover:bg-orange-600">
              <Download className="h-4 w-4" />Export CSV
            </button>
          </div>
        </div>
        <LeadFilters key={kind} config={config} filters={filters} setFilters={setFilters} onReset={() => setFilters({})} />
        <p data-testid="admin-result-count" className="text-sm text-slate-400">{data.total} {data.total === 1 ? "result" : "results"}</p>
        <LeadTable kind={kind} columns={config.columns} items={data.items} loading={loading} onReview={setReviewing} onDelete={user?.is_owner ? setDeleting : null} />
        <DeleteLeadDialog key={deleting?.id} kind={kind} lead={deleting} onClose={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); setRefreshKey((x) => x + 1); }} />
        <TelegramDialog open={telegramOpen} onClose={() => setTelegramOpen(false)} />
        <HousekeepingDialog open={hkOpen} onClose={() => setHkOpen(false)} />
        <PaymentsDialog open={payOpen} onClose={() => setPayOpen(false)} />
        <KycReviewDialog kind={kind} lead={reviewing} onClose={() => setReviewing(null)}
          onSaved={() => { setReviewing(null); setRefreshKey((x) => x + 1); }} />
        <TeamDialog open={teamOpen} onClose={() => setTeamOpen(false)} />
        <ActivityDialog open={activityOpen} onClose={() => setActivityOpen(false)} />
        <PricingDialog open={pricingOpen} onClose={() => setPricingOpen(false)} />
        <WalletsDialog open={walletsOpen} onClose={() => setWalletsOpen(false)} />
        <ExitsDialog open={exitsOpen} onClose={() => setExitsOpen(false)} onChange={loadExits} />
        <div className="flex items-center justify-end gap-3 text-sm">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="admin-page-prev"
            className="border border-white/15 px-4 py-2 disabled:opacity-40">Previous</button>
          <span data-testid="admin-page-indicator" className="font-mono text-slate-400">{page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="admin-page-next"
            className="border border-white/15 px-4 py-2 disabled:opacity-40">Next</button>
        </div>
      </main>
    </div>
  );
}
